import i18n from 'i18n/config';
import { useMemo, useState } from 'react';
import { MultiValue, SingleValue } from 'react-select';

import { SelectorOption, Society } from 'types';

import computableOperationTypes from 'constants/computableOperationTypes';
import operationTypeCountMap from 'constants/operationTypeCountMap';

type UseGetOperationTypeOptionsProps = {
  society: Society;
  onChangeTypes?: (types: string[]) => void; // eslint-disable-line
};

export const useGetOperationTypeOptions = ({
  society,
  onChangeTypes,
}: UseGetOperationTypeOptionsProps) => {
  const [selectedOperationTypeOptions, setSelectedOperationTypeOption] =
    useState<SelectorOption | null>(null);

  const operationTypeOptions = useMemo(
    () =>
      Object.values(computableOperationTypes)
        .map((operationType) => {
          const operationsCount =
            society?.operationsCount?.[
              operationTypeCountMap[
                operationType as keyof typeof operationTypeCountMap
              ] as keyof typeof society.operationsCount
            ] ?? 0;

          return {
            operationType,
            operationsCount,
          };
        })
        .filter(({ operationsCount }) => operationsCount > 0)
        .map(({ operationType }) => ({
          value: operationType,
          label: i18n.t(operationType),
        })),

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [i18n.language, society]
  );

  const handleChangeOperationTypeOptions = (
    selectedOption: MultiValue<SelectorOption> | SingleValue<SelectorOption>
  ) => {
    if (selectedOption instanceof Array) return;

    const types = selectedOption ? [selectedOption.value] : [];
    setSelectedOperationTypeOption(selectedOption);
    onChangeTypes?.(types);
  };

  return {
    data: {
      operationTypeOptions,
      selectedOperationTypeOptions,
    },
    actions: {
      handleChangeOperationTypeOptions,
    },
  };
};
